import { Box, Text } from '@wix/design-system';
import { ArrowLeftSmall, ArrowRightSmall, Article } from '@wix/wix-ui-icons-common';
import React, { FC, useCallback, useMemo } from 'react';

import { IArticleItem } from '../../../common/messageCreators';
import { useIsRtlText } from '../../hooks';
import { SelectorItem } from '../Selectors';
import { TextWithAutoDirection } from '../TextWithAutoDirection';

export type IArticleItemExtended = IArticleItem & Required<Pick<IArticleItem, 'subtitle'>>;

const isArticleExtended = (article: IArticleItem): article is IArticleItemExtended => !!article.subtitle;

const getExtendedViewBlocks = (article: IArticleItemExtended, isRtl: boolean) => [
  <Box direction="vertical">
    <Text size="medium" weight="normal" skin="primary">
      <TextWithAutoDirection text={article.title} />
    </Text>
    <Text size="small" weight="thin">
      <TextWithAutoDirection text={article.subtitle} />
    </Text>
  </Box>,
  <Box color="B10" paddingTop="3px">
    {isRtl ? <ArrowLeftSmall /> : <ArrowRightSmall />}
  </Box>,
];

const getRegularViewBlocks = (article: IArticleItem) => [
  <Box color="B10" verticalAlign="middle">
    <Text skin="primary">
      <Article size="24" />
    </Text>
  </Box>,
  <Text weight="normal" skin="primary" dataHook="label">
    <TextWithAutoDirection text={article.title} />
  </Text>,
];

type ArticleItemProps = {
  article: IArticleItem;
  index: number;
  onArticleClick: (article: IArticleItem, index: number) => void;
  subtitle?: string;
};

export const ArticleItem: FC<ArticleItemProps> = ({ article, index, onArticleClick }) => {
  const isRtl = useIsRtlText(article.title);

  const onClick = useCallback(() => {
    onArticleClick(article, index);
  }, [onArticleClick, article, index]);

  const columns = useMemo(() => {
    const isExtendedView = isArticleExtended(article);
    const contentBlocks = isExtendedView
      ? getExtendedViewBlocks(article, isRtl)
      : getRegularViewBlocks(article);
    const blocks = isRtl ? contentBlocks.reverse() : contentBlocks;

    return isExtendedView ? (
      <Box gap="SP1" align="space-between" direction="horizontal" dataHook="article-item">
        {blocks}
      </Box>
    ) : (
      <Box gap="SP3" align={isRtl ? 'right' : 'left'} dataHook="article-item">
        {blocks}
      </Box>
    );
  }, [article, isRtl]);

  return <SelectorItem onClick={onClick}>{columns}</SelectorItem>;
};
